'use client';

import { useEffect, useMemo, useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  createDataToolJson,
  createTextDiff,
  csvToJsonText,
  dedupeLines,
  extractCsvColumns,
  formatJsonText,
  jsonToCsvText,
  minifyJsonText,
  parseCsvText,
  removeEmptyLines,
  renderTextDiffHtml,
  sortLines,
  testRegexPattern,
  validateJsonText,
  type CsvParseResult,
} from '@/lib/browser-data-tools';

type Variant =
  | 'json-validator'
  | 'json-formatter'
  | 'json-minifier'
  | 'json-to-csv'
  | 'csv-to-json'
  | 'csv-viewer'
  | 'csv-column-extractor'
  | 'dedupe-lines'
  | 'sort-lines'
  | 'remove-empty-lines'
  | 'text-diff'
  | 'regex-tester-lite';

type ToolResult =
  | {
      kind: 'json';
      output: string;
      jsonLabel: string;
      sizeDelta?: string | null;
    }
  | {
      kind: 'csv';
      output: string;
      rowCount: number;
      columnCount: number;
    }
  | {
      kind: 'csv-view';
      parsed: CsvParseResult;
      rowCount: number;
      columnCount: number;
      previewRows: string[][];
    }
  | {
      kind: 'csv-columns';
      csv: string;
      json: string;
      headers: string[];
      rows: string[][];
      delimiter: CsvParseResult['delimiter'];
    }
  | {
      kind: 'text';
      output: string;
      note?: string | null;
    }
  | {
      kind: 'diff';
      html: string;
      summaryText: string;
    }
  | {
      kind: 'regex';
      source: string;
      flags: string;
      matches: ReturnType<typeof testRegexPattern>['matches'];
      jsonText: string;
    };

const sampleJson = `[
  {
    "name": "Cleanor",
    "team": "growth",
    "visits": 1200,
    "active": true
  },
  {
    "name": "Cleanor",
    "team": "content",
    "visits": 420,
    "active": false
  }
]`;

const sampleCsv = `name,team,visits,active
Cleanor,growth,1200,true
Cleanor,content,420,false`;

const sampleLines = `alpha
beta
alpha

gamma
beta`;

const sampleDiffLeft = `Line one
Line two stays
Old pricing copy`;

const sampleDiffRight = `Line one
Line two stays
New pricing copy
One more line`;

const sampleRegexText = `utm_source=google
utm_medium=cpc
utm_campaign=spring-launch`;

const toolCopy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel: string;
    successLabel: string;
    primaryPlaceholder: string;
    secondaryPlaceholder?: string;
    patternPlaceholder?: string;
    flagsPlaceholder?: string;
    downloadName?: string;
  }
> = {
  'json-validator': {
    title: 'Validate JSON and show a readable parsed result',
    description:
      'Paste JSON, validate that it parses, and keep one pretty-printed result ready for copy or download.',
    badge: 'JSON',
    actionLabel: 'Validate JSON',
    successLabel: 'JSON validation',
    primaryPlaceholder: sampleJson,
    downloadName: 'validated.json',
  },
  'json-formatter': {
    title: 'Format JSON with stable browser-side pretty printing',
    description:
      'Paste JSON, normalize the indentation locally, and export cleaner structured output without leaving the browser.',
    badge: 'Format',
    actionLabel: 'Format JSON',
    successLabel: 'JSON formatting',
    primaryPlaceholder: sampleJson,
    downloadName: 'formatted.json',
  },
  'json-minifier': {
    title: 'Minify JSON safely and compare the size delta',
    description:
      'Paste JSON, remove the unnecessary whitespace, and export a smaller result while preserving the same data.',
    badge: 'Minify',
    actionLabel: 'Minify JSON',
    successLabel: 'JSON minification',
    primaryPlaceholder: sampleJson,
    downloadName: 'minified.json',
  },
  'json-to-csv': {
    title: 'Convert flat JSON rows into CSV',
    description:
      'Paste one flat object or an array of flat objects, convert it into CSV locally, and reject nested data with a clear message.',
    badge: 'Convert',
    actionLabel: 'Convert to CSV',
    successLabel: 'JSON to CSV conversion',
    primaryPlaceholder: sampleJson,
    downloadName: 'converted.csv',
  },
  'csv-to-json': {
    title: 'Convert header-based CSV into flat JSON rows',
    description:
      'Paste CSV with a header row, convert it into an array of flat JSON objects locally, and export the structured result.',
    badge: 'Convert',
    actionLabel: 'Convert to JSON',
    successLabel: 'CSV to JSON conversion',
    primaryPlaceholder: sampleCsv,
    downloadName: 'converted.json',
  },
  'csv-viewer': {
    title: 'Inspect CSV rows, columns, and delimiter detection',
    description:
      'Paste CSV, detect the delimiter, preview the table, and review row and column counts without opening a spreadsheet app.',
    badge: 'CSV',
    actionLabel: 'View CSV',
    successLabel: 'CSV inspection',
    primaryPlaceholder: sampleCsv,
  },
  'csv-column-extractor': {
    title: 'Extract selected CSV columns into a smaller export',
    description:
      'Paste CSV, pick the header columns you need, and export both a filtered CSV and a matching JSON view locally.',
    badge: 'Extract',
    actionLabel: 'Extract columns',
    successLabel: 'CSV column extraction',
    primaryPlaceholder: sampleCsv,
  },
  'dedupe-lines': {
    title: 'Remove duplicate lines while preserving the first-seen order',
    description:
      'Paste one text block, dedupe repeated lines locally, and keep the original order of the first copy of each line.',
    badge: 'Clean',
    actionLabel: 'Remove duplicates',
    successLabel: 'Line dedupe',
    primaryPlaceholder: sampleLines,
    downloadName: 'deduped.txt',
  },
  'sort-lines': {
    title: 'Sort pasted lines with simple case controls',
    description:
      'Paste text lines, sort them ascending or descending, and choose whether the comparison stays case-sensitive.',
    badge: 'Sort',
    actionLabel: 'Sort lines',
    successLabel: 'Line sorting',
    primaryPlaceholder: sampleLines,
    downloadName: 'sorted.txt',
  },
  'remove-empty-lines': {
    title: 'Remove blank and whitespace-only lines',
    description:
      'Paste text, strip blank rows locally, and keep the remaining line order untouched.',
    badge: 'Clean',
    actionLabel: 'Remove empty lines',
    successLabel: 'Empty-line cleanup',
    primaryPlaceholder: sampleLines,
    downloadName: 'cleaned.txt',
  },
  'text-diff': {
    title: 'Compare two texts with line-first diff and word highlights',
    description:
      'Paste the original and updated text, inspect line changes with inline word-level highlights, and export a compact diff summary.',
    badge: 'Diff',
    actionLabel: 'Compare text',
    successLabel: 'Text diff',
    primaryPlaceholder: sampleDiffLeft,
    secondaryPlaceholder: sampleDiffRight,
    downloadName: 'text-diff-summary.txt',
  },
  'regex-tester-lite': {
    title: 'Test one JavaScript regular expression against pasted text',
    description:
      'Enter a JS regex pattern, optional flags, and test text to review matches, capture groups, and compile errors locally.',
    badge: 'Regex',
    actionLabel: 'Test regex',
    successLabel: 'Regex test',
    primaryPlaceholder: sampleRegexText,
    patternPlaceholder: 'utm_(source|medium|campaign)=([^\\n]+)',
    flagsPlaceholder: 'gm',
  },
};

function createTextDownload(text: string, fileName: string, mimeType = 'text/plain;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error('The browser could not access the clipboard.');
}

function formatCsvJsonText(text: string) {
  return JSON.stringify(JSON.parse(text), null, 2);
}

function buildCsvPreviewRows(parsed: CsvParseResult, limit = 8) {
  return parsed.rows.slice(0, limit);
}

function buildRegexPlainText(result: ReturnType<typeof testRegexPattern>) {
  if (!result.matches.length) {
    return 'No matches found.';
  }

  return result.matches
    .map((match, index) => {
      const groups = match.groups.length ? ` | groups: ${match.groups.join(', ')}` : '';
      return `#${index + 1} @ ${match.index}: ${match.match}${groups}`;
    })
    .join('\n');
}

export default function BrowserDataTool({ variant }: { variant: Variant }) {
  const copy = toolCopy[variant];
  const [inputValue, setInputValue] = useState('');
  const [secondaryValue, setSecondaryValue] = useState('');
  const [regexPattern, setRegexPattern] = useState('');
  const [regexFlags, setRegexFlags] = useState('');
  const [sortDescending, setSortDescending] = useState(false);
  const [sortCaseSensitive, setSortCaseSensitive] = useState(false);
  const [result, setResult] = useState<ToolResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedCsvHeaders, setSelectedCsvHeaders] = useState<string[]>([]);

  const parsedCsvForSelection = useMemo(() => {
    if (variant !== 'csv-column-extractor' || !inputValue.trim()) {
      return { parsed: null as CsvParseResult | null, error: null as string | null };
    }

    try {
      return { parsed: parseCsvText(inputValue), error: null as string | null };
    } catch (error) {
      return {
        parsed: null,
        error: error instanceof Error ? error.message : 'The CSV could not be parsed.',
      };
    }
  }, [inputValue, variant]);

  useEffect(() => {
    if (variant !== 'csv-column-extractor') {
      return;
    }

    if (!parsedCsvForSelection.parsed) {
      setSelectedCsvHeaders([]);
      return;
    }

    const parsed = parsedCsvForSelection.parsed;
    setSelectedCsvHeaders((current) => {
      const valid = current.filter((header) => parsed.headers.includes(header));
      return valid.length ? valid : parsed.headers;
    });
  }, [parsedCsvForSelection.parsed, variant]);

  function resetOutput() {
    setResult(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);
  }

  async function runTool() {
    const needsTwoTexts = variant === 'text-diff';
    const needsRegex = variant === 'regex-tester-lite';

    if (!inputValue.trim()) {
      setErrorMessage('Paste some input first.');
      return;
    }

    if (needsTwoTexts && !secondaryValue.trim()) {
      setErrorMessage('Paste the second text block first.');
      return;
    }

    if (needsRegex && !regexPattern) {
      setErrorMessage('Enter a regular expression pattern first.');
      return;
    }

    setIsRunning(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another data tool run right now.',
      );

      switch (variant) {
        case 'json-validator': {
          const output = validateJsonText(inputValue);
          setResult({ kind: 'json', output, jsonLabel: 'Validated JSON' });
          break;
        }
        case 'json-formatter': {
          const output = formatJsonText(inputValue);
          setResult({ kind: 'json', output, jsonLabel: 'Formatted JSON' });
          break;
        }
        case 'json-minifier': {
          const output = minifyJsonText(inputValue);
          const sizeDelta = `${inputValue.trim().length} chars -> ${output.length} chars`;
          setResult({ kind: 'json', output, jsonLabel: 'Minified JSON', sizeDelta });
          break;
        }
        case 'json-to-csv': {
          const output = jsonToCsvText(inputValue);
          const parsed = parseCsvText(output);
          setResult({
            kind: 'csv',
            output,
            rowCount: parsed.rows.length,
            columnCount: parsed.headers.length,
          });
          break;
        }
        case 'csv-to-json': {
          const output = csvToJsonText(inputValue);
          const parsed = JSON.parse(output) as unknown[];
          const firstRow = parsed[0] as Record<string, unknown> | undefined;
          setResult({
            kind: 'json',
            output,
            jsonLabel: 'Converted JSON',
            sizeDelta: `${parsed.length} rows • ${firstRow ? Object.keys(firstRow).length : 0} columns`,
          });
          break;
        }
        case 'csv-viewer': {
          const parsed = parseCsvText(inputValue);
          setResult({
            kind: 'csv-view',
            parsed,
            rowCount: parsed.rows.length,
            columnCount: parsed.headers.length,
            previewRows: buildCsvPreviewRows(parsed),
          });
          break;
        }
        case 'csv-column-extractor': {
          const extracted = extractCsvColumns(inputValue, selectedCsvHeaders);
          setResult({
            kind: 'csv-columns',
            csv: extracted.csv,
            json: extracted.json,
            headers: extracted.headers,
            rows: extracted.rows,
            delimiter: extracted.delimiter,
          });
          break;
        }
        case 'dedupe-lines': {
          const deduped = dedupeLines(inputValue);
          setResult({
            kind: 'text',
            output: deduped.text,
            note: `${deduped.removedCount} duplicate line${deduped.removedCount === 1 ? '' : 's'} removed`,
          });
          break;
        }
        case 'sort-lines': {
          const output = sortLines(inputValue, {
            descending: sortDescending,
            caseSensitive: sortCaseSensitive,
          });
          setResult({
            kind: 'text',
            output,
            note: `${sortDescending ? 'Descending' : 'Ascending'} sort • ${
              sortCaseSensitive ? 'Case-sensitive' : 'Case-insensitive'
            }`,
          });
          break;
        }
        case 'remove-empty-lines': {
          const output = removeEmptyLines(inputValue);
          const beforeCount = inputValue.split(/\r\n?|\n/).length;
          const afterCount = output ? output.split('\n').length : 0;
          setResult({
            kind: 'text',
            output,
            note: `${Math.max(beforeCount - afterCount, 0)} empty line${beforeCount - afterCount === 1 ? '' : 's'} removed`,
          });
          break;
        }
        case 'text-diff': {
          const diff = createTextDiff(inputValue, secondaryValue);
          setResult({
            kind: 'diff',
            html: renderTextDiffHtml(diff),
            summaryText: diff.summaryText,
          });
          break;
        }
        case 'regex-tester-lite': {
          const regexResult = testRegexPattern(regexPattern, regexFlags, inputValue);
          setResult({
            kind: 'regex',
            source: regexResult.source,
            flags: regexResult.flags,
            matches: regexResult.matches,
            jsonText: createDataToolJson(regexResult.matches),
          });
          break;
        }
      }

      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setResult(null);
      setErrorMessage(
        error instanceof Error ? error.message : 'This data tool could not complete the request.',
      );
    } finally {
      setIsRunning(false);
    }
  }

  async function handleCopy(text: string, label: string) {
    try {
      await copyText(text);
      setCopyMessage(label);
      setErrorMessage(null);
    } catch {
      setErrorMessage('The browser could not copy the current output.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{copy.badge}</span>
        <h2 className="tool-live-title">{copy.title}</h2>
        <p className="tool-live-subtitle">{copy.description}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {variant === 'regex-tester-lite' ? (
              <>
                <label className="tool-live-field">
                  <span>Regex pattern</span>
                  <input
                    onChange={(event) => {
                      resetOutput();
                      setRegexPattern(event.target.value);
                    }}
                    placeholder={copy.patternPlaceholder}
                    spellCheck={false}
                    type="text"
                    value={regexPattern}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Flags</span>
                  <input
                    onChange={(event) => {
                      resetOutput();
                      setRegexFlags(event.target.value);
                    }}
                    placeholder={copy.flagsPlaceholder}
                    spellCheck={false}
                    type="text"
                    value={regexFlags}
                  />
                </label>
              </>
            ) : null}

            <label className="tool-live-field tool-live-field-full">
              <span>
                {variant === 'text-diff'
                  ? 'Original text'
                  : variant === 'regex-tester-lite'
                    ? 'Test text'
                    : variant.includes('csv')
                      ? 'CSV input'
                      : variant.includes('json')
                        ? 'JSON input'
                        : 'Text input'}
              </span>
              <textarea
                onChange={(event) => {
                  resetOutput();
                  setInputValue(event.target.value);
                }}
                placeholder={copy.primaryPlaceholder}
                spellCheck={false}
                value={inputValue}
              />
            </label>

            {variant === 'text-diff' ? (
              <label className="tool-live-field tool-live-field-full">
                <span>Updated text</span>
                <textarea
                  onChange={(event) => {
                    resetOutput();
                    setSecondaryValue(event.target.value);
                  }}
                  placeholder={copy.secondaryPlaceholder}
                  spellCheck={false}
                  value={secondaryValue}
                />
              </label>
            ) : null}

            {variant === 'sort-lines' ? (
              <>
                <label className="tool-live-option">
                  <input
                    checked={sortDescending}
                    onChange={(event) => {
                      resetOutput();
                      setSortDescending(event.target.checked);
                    }}
                    type="checkbox"
                  />
                  <span>Sort descending</span>
                </label>
                <label className="tool-live-option">
                  <input
                    checked={sortCaseSensitive}
                    onChange={(event) => {
                      resetOutput();
                      setSortCaseSensitive(event.target.checked);
                    }}
                    type="checkbox"
                  />
                  <span>Case-sensitive compare</span>
                </label>
              </>
            ) : null}

            {variant === 'csv-column-extractor' && parsedCsvForSelection.parsed ? (
              <div className="tool-live-field tool-live-field-full">
                <span>Select columns</span>
                <div className="tool-data-chip-row">
                  {parsedCsvForSelection.parsed.headers.map((header) => {
                    const checked = selectedCsvHeaders.includes(header);
                    return (
                      <label className="tool-live-chip" key={header}>
                        <input
                          checked={checked}
                          onChange={(event) => {
                            resetOutput();
                            setSelectedCsvHeaders((current) =>
                              event.target.checked
                                ? [...current, header]
                                : current.filter((entry) => entry !== header),
                            );
                          }}
                          type="checkbox"
                        />
                        <span>{header}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="tool-live-panel-note">
                  <strong>Detected CSV</strong>
                  <span>
                    {parsedCsvForSelection.parsed.rows.length} rows •{' '}
                    {parsedCsvForSelection.parsed.headers.length} columns
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          <div className="tool-live-actions">
            <button
              className="button"
              disabled={isRunning}
              onClick={() => void runTool()}
              type="button"
            >
              {isRunning ? 'Working…' : copy.actionLabel}
            </button>
          </div>

          {variant === 'csv-column-extractor' && parsedCsvForSelection.error ? (
            <p className="status-message status-error">{parsedCsvForSelection.error}</p>
          ) : null}
          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="status-message status-success">{creditMessage}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <aside className="tool-live-panel">
          {result?.kind === 'json' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.output, `${result.jsonLabel} copied.`)}
                  type="button"
                >
                  Copy JSON
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.output,
                      copy.downloadName ?? 'output.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              {result.sizeDelta ? (
                <div className="tool-live-panel-note">
                  <strong>Summary</strong>
                  <span>{result.sizeDelta}</span>
                </div>
              ) : null}
              <div className="tool-live-preview">
                <h3>{result.jsonLabel}</h3>
                <pre className="tool-markdown-code">{result.output}</pre>
              </div>
            </>
          ) : null}

          {result?.kind === 'csv' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.output, 'CSV copied.')}
                  type="button"
                >
                  Copy CSV
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.output,
                      copy.downloadName ?? 'output.csv',
                      'text/csv;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download CSV
                </button>
              </div>
              <div className="tool-live-panel-note">
                <strong>Shape</strong>
                <span>
                  {result.rowCount} rows • {result.columnCount} columns
                </span>
              </div>
              <div className="tool-live-preview">
                <h3>CSV output</h3>
                <pre className="tool-markdown-code">{result.output}</pre>
              </div>
            </>
          ) : null}

          {result?.kind === 'csv-view' ? (
            <>
              <div className="tool-html-meta-grid">
                <div className="tool-live-panel-note">
                  <strong>Delimiter</strong>
                  <span>{result.parsed.delimiter === '\t' ? 'Tab' : result.parsed.delimiter}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Rows</strong>
                  <span>{result.rowCount}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Columns</strong>
                  <span>{result.columnCount}</span>
                </div>
              </div>
              <div className="tool-data-table-shell">
                <table className="tool-data-table">
                  <thead>
                    <tr>
                      {result.parsed.headers.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.previewRows.map((row, rowIndex) => (
                      <tr key={`row-${rowIndex}`}>
                        {result.parsed.headers.map((header, cellIndex) => (
                          <td key={`${rowIndex}-${header}`}>{row[cellIndex] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          {result?.kind === 'csv-columns' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.csv, 'Filtered CSV copied.')}
                  type="button"
                >
                  Copy CSV
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    void handleCopy(formatCsvJsonText(result.json), 'Filtered JSON copied.')
                  }
                  type="button"
                >
                  Copy JSON
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(result.csv, 'csv-columns.csv', 'text/csv;charset=utf-8')
                  }
                  type="button"
                >
                  Download CSV
                </button>
              </div>
              <div className="tool-live-panel-note">
                <strong>Extracted</strong>
                <span>
                  {result.rows.length} rows • {result.headers.length} columns
                </span>
              </div>
              <div className="tool-data-table-shell">
                <table className="tool-data-table">
                  <thead>
                    <tr>
                      {result.headers.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.slice(0, 8).map((row, rowIndex) => (
                      <tr key={`extract-row-${rowIndex}`}>
                        {result.headers.map((header, cellIndex) => (
                          <td key={`${rowIndex}-${header}`}>{row[cellIndex] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="tool-live-preview">
                <h3>JSON view</h3>
                <pre className="tool-markdown-code">{formatCsvJsonText(result.json)}</pre>
              </div>
            </>
          ) : null}

          {result?.kind === 'text' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.output, 'Text output copied.')}
                  type="button"
                >
                  Copy output
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(result.output, copy.downloadName ?? 'output.txt')
                  }
                  type="button"
                >
                  Download text
                </button>
              </div>
              {result.note ? (
                <div className="tool-live-panel-note">
                  <strong>Summary</strong>
                  <span>{result.note}</span>
                </div>
              ) : null}
              <div className="tool-live-preview">
                <h3>Output</h3>
                <pre className="tool-markdown-code">{result.output}</pre>
              </div>
            </>
          ) : null}

          {result?.kind === 'diff' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.summaryText, 'Diff summary copied.')}
                  type="button"
                >
                  Copy summary
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(result.summaryText, copy.downloadName ?? 'diff.txt')
                  }
                  type="button"
                >
                  Download summary
                </button>
              </div>
              <div className="tool-live-panel-note">
                <strong>Summary</strong>
                <span>{result.summaryText.replace(/\n/g, ' • ')}</span>
              </div>
              <div
                className="tool-markdown-diff"
                dangerouslySetInnerHTML={{ __html: result.html }}
              />
            </>
          ) : null}

          {result?.kind === 'regex' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() =>
                    void handleCopy(buildRegexPlainText(result), 'Regex matches copied.')
                  }
                  type="button"
                >
                  Copy matches
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      'regex-matches.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              <div className="tool-html-meta-grid">
                <div className="tool-live-panel-note">
                  <strong>Source</strong>
                  <span>{result.source}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Flags</strong>
                  <span>{result.flags || 'None'}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Matches</strong>
                  <span>{result.matches.length}</span>
                </div>
              </div>
              <div className="tool-html-issue-list">
                {result.matches.length ? (
                  result.matches.map((match, index) => (
                    <article className="tool-data-regex-match" key={`${match.index}-${index}`}>
                      <strong>Match {index + 1}</strong>
                      <span>Index {match.index}</span>
                      <code>{match.match}</code>
                      {match.groups.length ? <p>Groups: {match.groups.join(', ')}</p> : null}
                    </article>
                  ))
                ) : (
                  <article className="tool-data-regex-match">
                    <strong>No matches found</strong>
                    <p>The pattern compiled, but it did not match the current test text.</p>
                  </article>
                )}
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
